import moment from 'moment';
import { mixed, object, string } from 'yup';

import { API_DATE_FORMAT, MONTH_YEAR_DISPLAY_FORMAT } from '../../../constants';

import type { Moment } from 'moment';
import type {
  IReferral,
  ReferralFieldName,
  ReferralFormLimits,
  ReferralFormValues,
  ReferralPayload,
  ReferralRowDescriptor,
} from './types';

export function toReferralPayload(values: ReferralFormValues): ReferralPayload {
  return {
    referral_date: values.referral_date?.format(API_DATE_FORMAT) ?? null,
    provider_name: values.provider_name,
    diagnosis_tool: values.diagnosis_tool,
    city: values.city,
    state: values.state,
  };
}

export function toReferralFieldPayload<K extends ReferralFieldName>(
  name: K,
  values: ReferralFormValues,
): ReferralPayload {
  const data: ReferralPayload = {};
  data[name] = toReferralPayload(values)[name];
  return data;
}

export function mapReferralRowToValues(row: IReferral): ReferralFormValues {
  return {
    referral_date: row.referral_date ? moment(row.referral_date) : null,
    provider_name: row.provider_name ?? '',
    diagnosis_tool: row.diagnosis_tool ?? '',
    city: row.city ?? '',
    state: row.state ?? '',
  };
}

export function getReferralDeleteMessage(row: ReferralRowDescriptor): string {
  if (!row.provider_name) {
    return 'Are you sure you want to delete this referral?';
  }
  const referralDescriptor = row.referral_date
    ? `${row.provider_name} on ${moment(row.referral_date).format(MONTH_YEAR_DISPLAY_FORMAT)}`
    : row.provider_name;
  return `Are you sure you want to delete referral "${referralDescriptor}"?`;
}

export function buildReferralFormSchema(limits: ReferralFormLimits) {
  let citySchema = string().trim().required();
  if (limits.cityMinLength) {
    citySchema = citySchema.min(
      limits.cityMinLength,
      `City must be at least ${limits.cityMinLength} characters`,
    );
  }
  return object({
    referral_date: mixed<Moment>().nullable().required(),
    provider_name: string()
      .trim()
      .min(
        limits.providerNameMinLength,
        `Provider name must be at least ${limits.providerNameMinLength} characters`,
      )
      .required(),
    diagnosis_tool: string().required(),
    city: citySchema,
    state: string().required(),
  });
}
